import { isValid } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { parsePhoneNumber } from "libphonenumber-js";

import {
  Country,
  CurrencyResponse,
  FieldType,
  PhoneNumberResponse,
} from "@smart/bridge-types-basic";
import {
  entriesOf,
  groupBy,
  isNotNullOrUndefined,
  isNullOrUndefined,
  sortField,
} from "@smart/itops-utils-basic";

import {
  basicLayoutProviderId,
  cuiLayoutId,
  Field,
  Group,
  LayoutField,
  Matter,
  MatterItem,
  MatterItemList,
  SBContactType,
  Section,
} from "./types";

export const convertCurrencyToSB = (currency: CurrencyResponse) =>
  currency && typeof currency === "object"
    ? currency.value
    : `${currency || ""}`;

export const convertPhoneNumberToSB = (
  country: Country,
  phoneNumber?: PhoneNumberResponse,
) => {
  switch (country) {
    case "US": {
      const isDomestic = phoneNumber?.countryCallingCode === "1";
      const match = phoneNumber?.formattedNumber?.match(/\((\d{3})\) (.+)/);
      return isDomestic
        ? { areaCode: match?.[1], number: match?.[2] || phoneNumber?.number }
        : { number: phoneNumber?.number };
    }
    case "AU": {
      const isDomestic = phoneNumber?.countryCallingCode === "61";
      return {
        number: isDomestic ? phoneNumber.formattedNumber : phoneNumber?.number,
      };
    }
    case "GB": {
      const isDomestic = phoneNumber?.countryCallingCode === "44";
      return {
        number: isDomestic ? phoneNumber.formattedNumber : phoneNumber?.number,
      };
    }
    default:
      return { number: phoneNumber?.number };
  }
};

export const convertPhoneNumberFromSB = (
  country: Country,
  value?: string | Record<string, any>,
) => {
  const number =
    typeof value === "string"
      ? value
      : [value?.areaCode, value?.number].filter(Boolean).join("");

  try {
    const parsed = parsePhoneNumber(number, country);

    return {
      number: parsed?.number || number || null,
      countryCallingCode: parsed?.countryCallingCode || null,
      formattedNumber: parsed?.formatNational() || number || null,
      country: parsed?.country || null,
    };
  } catch (error) {
    return {
      number: null,
      countryCallingCode: null,
      formattedNumber: null,
      country: null,
    };
  }
};

export const convertDateToSB = (value: string | null | undefined) => {
  if (isNullOrUndefined(value)) return value;

  const date = new Date(value);

  return isValid(date)
    ? formatInTimeZone(date, "UTC", "yyyy-MM-dd")
    : undefined;
};

export const getContactInfo = (fieldName: string): [SBContactType, string] => {
  const [contactType, ...keys] = (fieldName.split("/") || []) as [
    SBContactType,
    string,
  ];
  const key = keys.join("/");

  return [contactType, key];
};

export const getClientRoleName = (
  sections: Section[],
  fields: Field[],
  formMatterTypeRepresentatives: string[] | undefined,
) => {
  const sortedSections = sortField(sections, {
    key: (f) => f.order,
    dir: "asc",
  });

  for (const section of sortedSections) {
    const sortedFields = sortField(
      fields.filter((f) => f.sectionUri === section.uri),
      { key: (f) => f.order, dir: "asc" },
    );

    const layoutId = sortedFields.find(
      ({ layout }) =>
        !!layout?.id && formMatterTypeRepresentatives?.includes(layout.id),
    )?.layout?.id;

    if (layoutId) return layoutId;
  }

  return undefined;
};

export const getMatterTypeRepresentatives = (
  matterTypes: { representativeOptions?: string[] }[],
) => [
  ...new Set(
    matterTypes
      .map(({ representativeOptions }) => representativeOptions)
      .flat()
      .filter(isNotNullOrUndefined),
  ),
];

export const getLayoutFieldName = (
  formLayoutField: LayoutField,
  matterLayoutFields: LayoutField[],
  type: FieldType,
): string | null => {
  for (const matterLayoutField of matterLayoutFields) {
    const basedMatterLayoutFieldName = matterLayoutField.name
      .replaceAll("[]", "")
      .toLowerCase();
    const basedFormLayoutFieldName = formLayoutField.name
      .replaceAll("[]", "")
      .toLowerCase();

    if (basedMatterLayoutFieldName === basedFormLayoutFieldName)
      return matterLayoutField.name;

    const consolidatedTypes: FieldType[] = ["address", "choice", "checkbox"];
    const matterLayoutFieldPathSize =
      basedMatterLayoutFieldName.split("/").length;
    const formLayoutFieldPathSize = basedFormLayoutFieldName.split("/").length;
    // Consolidated form fields should have only one extra suffixed option.
    if (
      consolidatedTypes.includes(type) &&
      basedMatterLayoutFieldName.startsWith(basedFormLayoutFieldName) &&
      matterLayoutFieldPathSize === formLayoutFieldPathSize + 1
    ) {
      const nameParts = matterLayoutField.name.split("/");
      const possibleValue = (nameParts.pop() || "").replace("[]", "");
      if (
        type === "address" ||
        formLayoutField.possibleValues?.includes(possibleValue)
      ) {
        return nameParts.join("/");
      }
    }
  }

  return null;
};

type IndexLocation = "left-most" | "right-most";
type BuildIndexOptions = {
  location: IndexLocation;
};

export const buildLayoutFieldName = ({
  name,
  index = 0,
  overrideClientIndex,
  options = { location: "right-most" },
}: {
  name: string;
  index?: number;
  overrideClientIndex?: {
    index: number;
    checkShouldOverride: (nameParts: string[]) => boolean;
  };
  options?: BuildIndexOptions;
}) => {
  if (!name.includes("[]")) return name;

  const nameParts =
    options.location === "right-most"
      ? name.split("/").reverse()
      : name.split("/");

  let first = false;
  let found = false;
  const indexedParts = nameParts.map((part) => {
    if (part.endsWith("[]") && !found) {
      first = true;
      found = true;
    } else {
      first = false;
    }

    return part.replace("[]", first ? `[${index}]` : "[0]");
  });

  let indexedPartsToUse = indexedParts;
  if (
    overrideClientIndex &&
    overrideClientIndex.checkShouldOverride(nameParts)
  ) {
    const { index: overrideIndex } = overrideClientIndex;
    const repeatableRegex = /\[\d*\]$/;
    // We assume the client part is the first repeating part from the left
    const clientNamePartIndex =
      options.location === "right-most"
        ? indexedParts.findLastIndex((part) => repeatableRegex.test(part))
        : indexedParts.findIndex((part) => repeatableRegex.test(part));
    indexedPartsToUse = indexedParts.map((part, idx) =>
      clientNamePartIndex === idx
        ? part.replace(/\[\d*\]/, `[${overrideIndex}]`)
        : part,
    );
  }

  return options.location === "right-most"
    ? indexedPartsToUse.reverse().join("/")
    : indexedPartsToUse.join("/");
};

export const buildCheckSubLayoutMapsToIndividualClient =
  ({ isGroupRepeatable }: { isGroupRepeatable: boolean }) =>
  (nameParts: string[]) => {
    const numOfRepeatingPart = nameParts.filter((part) =>
      /\[\d*\]$/.test(part),
    ).length;
    if (!numOfRepeatingPart) return false;
    /**
     * We can't match the client role id because the client role in the field name
     * is not exactly the same as the client role id.
     * For example, the client role id is "Client" but the field name is "Clients[]"
     * in "Matter/Clients[]/EstatePlanningDetails/PowerOfAttorneyGeneralDetails/CommencementOther".
     * If there are more than 1 repeating part, we assume the field is nested under the client.
     */
    if (numOfRepeatingPart > 1) return true;
    /**
     * If it's not a repeatable sub-layout nested under the client,
     * we only map to the receiving client if the group is not repeatable
     * as repeatable group represents the intention to map to multiple clients.
     */
    return !isGroupRepeatable;
  };

export const getLayoutId = (field: Field) => {
  if (!field.layout) return undefined;
  const cuiLayoutIdToUse = cuiLayoutId(field.layout);
  const basicLayoutId =
    field.layout.providerId === basicLayoutProviderId
      ? field.layout.id
      : undefined;
  return cuiLayoutIdToUse || basicLayoutId;
};

export const getClientRoleId = (
  existingRoles: { isClient: boolean; name: string }[],
): string | undefined => existingRoles?.find((r) => r.isClient)?.name;

export const getSentToIndividualClientIndex = (
  existingRoles: {
    isClient: boolean;
    name: string;
    contactId?: string | null;
  }[],
  recipientContactId: string | null | undefined,
): number => {
  const clientRoles = existingRoles?.filter((r) => r.isClient);
  if (!clientRoles?.length || clientRoles.length < 2) return -1;
  return clientRoles.findIndex(
    (r) =>
      r.contactId && recipientContactId && r.contactId === recipientContactId,
  );
};

export const getFieldUrisMappedToIndividualClient = async ({
  fields,
  groups,
  clientRoleId,
  sentToIndividualClientIndex = -1,
  getLayoutFields,
}: {
  fields: Field[];
  groups: Group[] | undefined;
  clientRoleId: string | undefined;
  sentToIndividualClientIndex?: number;
  getLayoutFields: ({
    layoutId,
    providerId,
  }: {
    layoutId: string;
    providerId: string;
  }) => Promise<LayoutField[]>;
}) => {
  if (sentToIndividualClientIndex === -1) return [];
  const clientSubLayoutFields = fields.filter(
    (f) =>
      f.field &&
      clientRoleId &&
      f.layout?.parentId &&
      f.layout.parentId === clientRoleId,
  );
  const loadedLayoutFields: Record<string, Record<string, LayoutField[]>> = {};

  const loadLayoutFields = async (
    layoutId: string | undefined,
    providerId: string,
  ) => {
    if (!layoutId) return;
    const loaded = loadedLayoutFields[providerId]?.[layoutId];
    if (loaded) return;

    const layoutFields = await getLayoutFields({
      layoutId,
      providerId,
    });

    loadedLayoutFields[providerId] = {
      ...loadedLayoutFields[providerId],
      [layoutId]: layoutFields,
    };
  };

  // Load layout fields sequentially to avoid rate limiting
  for (const field of clientSubLayoutFields) {
    await loadLayoutFields(getLayoutId(field), field.layout!.providerId);
  }

  return clientSubLayoutFields
    .map((f) => {
      const layoutId = getLayoutId(f);
      const layoutFields = layoutId
        ? loadedLayoutFields[f.layout!.providerId]?.[layoutId]
        : [];
      const originalLayoutFieldName = getLayoutFieldName(
        f.field!,
        layoutFields,
        f.type,
      );
      if (!originalLayoutFieldName) return null;
      const nameParts = originalLayoutFieldName.split("/").reverse();
      const isMappingToIndividualClient =
        buildCheckSubLayoutMapsToIndividualClient({
          isGroupRepeatable: f.groupUri
            ? !!groups?.find((g) => g.uri === f.groupUri)?.repeatable
            : false,
        })(nameParts);
      return isMappingToIndividualClient ? f : null;
    })
    .filter(Boolean)
    .map((f) => f!.uri);
};

export const groupLayoutItemsByClientRole = ({
  items,
  shouldGroup,
}: {
  items: Record<string, any>;
  shouldGroup: boolean;
}) => {
  if (!shouldGroup) return [items];

  // Group items by client index (number in first [] from left)
  const groupedItems: Record<number, Record<string, any>> = {};

  for (const [key, value] of Object.entries(items)) {
    const match = key.match(/\[(\d+)\]/);
    if (match) {
      const clientIndex = parseInt(match[1], 10);
      groupedItems[clientIndex] = {
        ...groupedItems[clientIndex],
        [key]: value,
      };
    } else {
      // If no client index found, put in group 0
      groupedItems[0] = {
        ...groupedItems[0],
        [key]: value,
      };
    }
  }

  // The grouped items have the same index as the client index.
  const maxIndex = Math.max(...Object.keys(groupedItems).map(Number), -1);
  const result: Record<string, any>[] = [];
  Array.from({ length: maxIndex + 1 }).forEach((_, i) => {
    const groupItems = groupedItems[i] || {};
    /**
     * We need to replace the client index with [0] as the
     * API doesn't work if the index is not 0 even when saving
     * layout items to the second client for example.
     */
    const normalizedItems: Record<string, any> = {};
    for (const [key, value] of Object.entries(groupItems)) {
      const normalizedKey = key.replace(/\[(\d*)\]/, "[0]");
      normalizedItems[normalizedKey] = value;
    }
    result[i] = normalizedItems;
  });

  return result;
};

export const groupLayoutItemsByCrossRoleRepeatable = ({
  groupedLayoutItems,
  crossRoleRepeatableItems,
}: {
  groupedLayoutItems: Record<string, any>[];
  crossRoleRepeatableItems?: Record<string, any>[];
}): Record<string, any>[] => {
  if (!crossRoleRepeatableItems?.length) return groupedLayoutItems;

  const result: Record<string, any>[] = [];
  const maxLength = Math.max(
    groupedLayoutItems.length,
    crossRoleRepeatableItems.length,
  );

  for (let i = 0; i < maxLength; i += 1) {
    const groupedItems = groupedLayoutItems[i] || {};
    const crossRoleItems = crossRoleRepeatableItems[i] || {};

    result[i] = {
      ...groupedItems,
      ...crossRoleItems,
    };
  }

  return result;
};

export const buildMatterItemList = (matter: Matter): MatterItemList =>
  entriesOf(matter.items || {}).flatMap(([, value]) =>
    value.map((item) => ({
      ...item,
      subItems: entriesOf(item.subItems || {}).flatMap(
        ([, subItem]) => subItem,
      ),
    })),
  );

export const buildExcludingLayoutItems = ({
  recipientContactId,
  matterItems,
}: {
  recipientContactId?: string;
  matterItems: MatterItemList;
}): MatterItem[] => {
  if (!recipientContactId) return [];

  const matterClientRoleItems = matterItems.filter(
    (matterItem) =>
      matterItem.type === "role" && matterItem.isClient && matterItem.role,
  );

  const groupByRoleName = groupBy(matterClientRoleItems, (item) => item.role!);
  for (const [, items] of entriesOf(groupByRoleName)) {
    // It has to contain the role for the recipient and has more than 1 role.
    if (
      items.length > 1 &&
      !!items.find((item) => item.contact?.id === recipientContactId)
    ) {
      const roleItemsExcludingRecipient = items.filter(
        (item) => item.contact?.id !== recipientContactId,
      );

      return roleItemsExcludingRecipient
        .flatMap(({ subItems }) =>
          subItems?.filter((subItem) => subItem?.type === "layout"),
        )
        .filter(isNotNullOrUndefined);
    }
  }

  return [];
};

export const removeRepeatableFieldIndicator = (fieldName: string): string =>
  fieldName?.endsWith("[]")
    ? fieldName.substring(0, fieldName.length - 2)
    : fieldName;

export const buildIndexedLayoutFieldName = ({
  fieldName,
  originalFieldName,
  index,
}: {
  fieldName: string;
  originalFieldName?: string;
  index: number;
}): string => {
  const lastBracketPairIndex = originalFieldName
    ? originalFieldName.lastIndexOf("[]")
    : -1;
  if (index === 0 && lastBracketPairIndex === -1) return fieldName;

  const indexedBaseFieldName = `${removeRepeatableFieldIndicator(fieldName)}[${index}]`;
  if (lastBracketPairIndex === -1) return indexedBaseFieldName;

  return `${originalFieldName!.substring(0, lastBracketPairIndex)}[${index}]${originalFieldName!.substring(lastBracketPairIndex + 2)}`;
};

export const layoutContactKeyCandidates = (
  indexedLayoutFieldName: string,
): string[] => {
  const unindexed = indexedLayoutFieldName.replace(/\[0\]/g, "");
  return unindexed === indexedLayoutFieldName
    ? [indexedLayoutFieldName]
    : [indexedLayoutFieldName, unindexed];
};

export const findByLayoutContactKey = <T extends { key: string }>(
  items: readonly T[] | undefined,
  indexedLayoutFieldName: string,
): T | undefined =>
  items?.find((item) =>
    layoutContactKeyCandidates(indexedLayoutFieldName).includes(item.key),
  );

export const getByLayoutContactKey = <T>(
  map: Record<string, T>,
  indexedLayoutFieldName: string | undefined,
): T | undefined => {
  if (!indexedLayoutFieldName) return undefined;
  for (const key of layoutContactKeyCandidates(indexedLayoutFieldName)) {
    const found = map[key];
    if (found) return found;
  }
  return undefined;
};
